var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import "./PlayerStyles.css";
import "../../styles/sprite.css";
import "../../styles/general.css";
import "../../components/views/buttons/buttonViewStyles.css";
import { getProgresStats, updateProgress } from "./PlayerService";
import { createShakaPlayer } from "../../utils/ShakaPlayer";
import { installCobaltBufferedShim, needsStallWatchdog, shimVideoBuffered, } from "../../utils/CobaltBuffered";
import { useRef, useState, useEffect, useCallback } from "react";
import { useFocusable } from "../../utils/Helpers";
import { useRouter } from "../../context/Router";
import { LoadingSpinner } from "../../components/views/loading-spinner/LoadingSpinner";
import { PlayerProgress } from "../../components/player/PlayerProgress";
import { PlayerControls } from "../../components/player/PlayerControls";
import { usePlayerStateActions } from "../../context/PlayerStateContext";
import { usePlayerErrorMessageActions } from "../../context/PlayerErrorMessageContext";
import { focusManager } from "../../utils/Focus";
import { useAuth } from "../../context/Auth";
import { useTranslation } from "react-i18next";
import { useExitModal } from "../../context/ExitModalContext";
import { IdlePromptModal } from "../../components/views/modals/IdlePromptModal";
export var MovieQualityEnum;
(function (MovieQualityEnum) {
    MovieQualityEnum["HD"] = "HD";
    MovieQualityEnum["SD"] = "SD";
})(MovieQualityEnum || (MovieQualityEnum = {}));
// Work around Cobalt's broken TimeRanges before any SourceBuffer is created.
// This patches SourceBuffer rather than Shaka, so it belongs at module scope.
// It no-ops on Cobalt 21+ and in a desktop browser — see CobaltBuffered.
installCobaltBufferedShim();
// shaka.util.Error.Severity.CRITICAL. RECOVERABLE (1) means Shaka is still
// retrying — segment fetches retry forever and never escalate — so only 2 is
// worth ending playback over.
var SHAKA_SEVERITY_CRITICAL = 2;
// --- Stall watchdog tuning (Cobalt below 21) ---
// Shaka 2.5.23's own StallDetector nudges currentTime by 0.1s after 1s of no
// progress, and on these boxes that nudge does not always take. This is the
// second tier: it waits well past Shaka's attempt, then escalates to a backward
// seek, forcing the full rebuffer Shaka never asks for. It samples currentTime
// and readyState only, so the buffered mask cannot influence it.
var WATCHDOG_INTERVAL_MS = 2000;
var FIRST_STALL_TIMEOUT_MS = 3000;
var STALL_TIMEOUT_MS = 5000;
var STALL_EPSILON_SECONDS = 0.25;
var STALL_NUDGE_SECONDS = 0.5;
var STALL_REBUFFER_SECONDS = 3;
var MAX_FORWARD_NUDGES = 2;
var MAX_REBUFFER_STEPS = 3;
var IDLE_TIMEOUT_MS = 3 * 60 * 60 * 1000;
var DIGIT_CODES = [
    "Digit0",
    "Digit1",
    "Digit2",
    "Digit3",
    "Digit4",
    "Digit5",
    "Digit6",
    "Digit7",
    "Digit8",
    "Digit9",
];
export var Player = function (_a) {
    var watchData = _a.watchData, lastInputAtRef = _a.lastInputAtRef;
    var _b = useRouter(), goBack = _b.goBack, navigate = _b.navigate;
    var isExitModalOpen = useExitModal().isExitModalOpen;
    var profile = useAuth().profile;
    var updateStateProgress = usePlayerStateActions().updateStateProgress;
    var errorActions = usePlayerErrorMessageActions();
    var t = useTranslation().t;
    var videoRef = useRef(null);
    var playerRef = useRef(null);
    var _c = useState({
        movieId: null,
        episodeId: null,
        episodeDetails: null,
        watchSessionId: null,
        movie: null,
        profileId: profile.id,
        source: { src: "", license: "" },
        playedSegments: [],
        progress: 0,
        quality: null,
        language: null,
        caption: null,
        loaded: false,
        videoTracks: null,
        audioTracks: null,
        textTracks: null,
        playing: false,
        ended: false,
        seasons: [],
        lastWatchedSeason: null,
        mappers: null,
    }), playerState = _c[0], setPlayerState = _c[1];
    var progressRef = useRef((watchData === null || watchData === void 0 ? void 0 : watchData.progress) || 0);
    var currentTimeRef = useRef((watchData === null || watchData === void 0 ? void 0 : watchData.currentTime) || 0);
    var _d = useState(true), loading = _d[0], setLoading = _d[1];
    var _e = useState(true), showControls = _e[0], setShowControls = _e[1];
    var controlsTimerRef = useRef(null);
    var cleanupRef = useRef(null);
    var digitSeekTargetRef = useRef(-1);
    var _f = useState(false), showIdlePrompt = _f[0], setShowIdlePrompt = _f[1];
    var idleTimerRef = useRef(null);
    var idlePromptOpenRef = useRef(false);
    var idleDisabledRef = useRef(false);
    var idleWasPlayingRef = useRef(false);
    // --- Controls auto-hide ---
    var resetControlsTimer = useCallback(function () {
        setShowControls(true);
        if (controlsTimerRef.current)
            clearTimeout(controlsTimerRef.current);
        controlsTimerRef.current = setTimeout(function () { return setShowControls(false); }, 5000);
    }, []);
    useEffect(function () {
        resetControlsTimer();
        return function () {
            if (controlsTimerRef.current)
                clearTimeout(controlsTimerRef.current);
        };
    }, [resetControlsTimer]);
    // --- Idle prompt ---
    var resetIdleTimer = useCallback(function () {
        if (idlePromptOpenRef.current || idleDisabledRef.current)
            return;
        if (idleTimerRef.current)
            clearTimeout(idleTimerRef.current);
        var delay = Math.max(0, IDLE_TIMEOUT_MS - (Date.now() - lastInputAtRef.current));
        idleTimerRef.current = setTimeout(function () {
            idleTimerRef.current = null;
            if (idlePromptOpenRef.current || idleDisabledRef.current)
                return;
            idleWasPlayingRef.current =
                !!videoRef.current &&
                    !videoRef.current.paused &&
                    !videoRef.current.ended;
            if (videoRef.current)
                videoRef.current.pause();
            if (isExitModalOpen())
                return;
            idlePromptOpenRef.current = true;
            focusManager.pushLayer({ groupName: "exitModal", index: 0 });
            setShowIdlePrompt(true);
        }, delay);
    }, [isExitModalOpen]);
    useEffect(function () {
        if (playerState.loaded)
            resetIdleTimer();
        return function () {
            if (idleTimerRef.current)
                clearTimeout(idleTimerRef.current);
            if (idlePromptOpenRef.current) {
                focusManager.popLayer();
                idlePromptOpenRef.current = false;
            }
        };
    }, [resetIdleTimer, playerState.loaded]);
    var handleIdleYes = useCallback(function () {
        focusManager.popLayer();
        idlePromptOpenRef.current = false;
        setShowIdlePrompt(false);
        if (videoRef.current && idleWasPlayingRef.current)
            videoRef.current.play();
        lastInputAtRef.current = Date.now();
        resetIdleTimer();
    }, [resetIdleTimer]);
    var handleIdleNo = useCallback(function () {
        focusManager.popLayer();
        idlePromptOpenRef.current = false;
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        navigate("home");
    }, [navigate]);
    // --- Focus setup ---
    useEffect(function () {
        if (playerState.loaded)
            return;
        focusManager.registerGroup({ name: "playerClose", position: 5 });
        focusManager.manualFocus("playerClose", 0);
        var t = setTimeout(function () { return focusManager.manualFocus("playerControls", 0); }, 0);
        return function () { return clearTimeout(t); };
    }, [playerState.loaded]);
    // --- Sync watchData into playerState ---
    useEffect(function () {
        setPlayerState(function (prev) { return (__assign(__assign({}, prev), watchData)); });
        currentTimeRef.current = (watchData === null || watchData === void 0 ? void 0 : watchData.currentTime) || 0;
        progressRef.current = (watchData === null || watchData === void 0 ? void 0 : watchData.progress) || 0;
    }, [watchData]);
    // --- Player lifecycle ---
    //
    // Startup sequence:
    //   1. Create Shaka player (sync)
    //   2. Attach ALL event listeners to <video>
    //   3. Call player.load() — Shaka begins buffering
    //   4. First canplay: seek to saved position
    //   5. seeked: call video.play() from correct position, mark ready
    //
    // If no saved position (targetTime=0), steps 4-5 are skipped:
    //   first canplay plays immediately and marks ready.
    //
    // Buffer detection uses a 1200ms debounce — short micro-buffers
    // don't flash the spinner.
    useEffect(function () {
        var _a;
        var video = videoRef.current;
        if (!playerState.movie || !playerState.episodeId || !video)
            return;
        if (!((_a = playerState.source) === null || _a === void 0 ? void 0 : _a.src))
            return;
        // Mask the spurious-empty video.buffered on the Cobalt majors that still
        // have the bug. Shaka 2.5.23's own stall recovery reads this and gives up
        // when it reads empty, so the shim is what keeps that recovery working.
        shimVideoBuffered(video);
        // Clean up previous player instance
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        setLoading(true);
        // --- State flags (local to this effect, not React state) ---
        var ready = false;
        var initialCanPlayFired = false;
        var targetTime = progressRef.current >= 0.95 ? 0 : currentTimeRef.current || 0;
        var lastTimeCheckSecond = 0;
        // Raised once per player instance, by showFatalError below.
        var gaveUp = false;
        // Every fatal path ends in the same modal, and each one has to isolate focus
        // behind a layer first: the modal's own dismissal pops a layer, so skipping
        // the push pops one belonging to the screen underneath and leaves its focus
        // in a stale group.
        var showFatalError = function () {
            if (gaveUp)
                return;
            gaveUp = true;
            idleDisabledRef.current = true;
            if (idleTimerRef.current)
                clearTimeout(idleTimerRef.current);
            if (idlePromptOpenRef.current) {
                focusManager.popLayer();
                idlePromptOpenRef.current = false;
                setShowIdlePrompt(false);
            }
            focusManager.pushLayer({ groupName: "modalView", index: 0 });
            errorActions.show(t("player.playback_failed"), "text-danger", null);
            errorActions.returnTo("back", t("layout.back_home"));
        };
        // --- 1. Create Shaka player ---
        // WatchScreen loads Shaka alongside the movie/episode fetch and does not
        // render this component until it is ready, so the build is here, with its
        // polyfills already installed by loadShaka(). Shaka's own API stays behind
        // createShakaPlayer — everything below speaks the facade's shape.
        // There is no error boundary in the app, so a throw here would take the
        // whole screen down rather than showing the player's own failure message.
        var player;
        try {
            player = createShakaPlayer(video);
        }
        catch (error) {
            console.error("Player create error:", error);
            showFatalError();
            return;
        }
        playerRef.current = player;
        // --- 2. Event handlers ---
        var setReady = function () {
            ready = true;
            setLoading(false);
            var dur = video.duration;
            var progress = dur > 0 ? Math.round((video.currentTime / dur) * 100) / 100 : 0;
            progressRef.current = progress;
            setPlayerState(function (s) { return (__assign(__assign({}, s), { playing: true, loaded: true, progress: progress })); });
        };
        var onCanPlay = function () {
            if (!initialCanPlayFired) {
                initialCanPlayFired = true;
                // Only resume to a stored currentTime that is known-good: finite, and
                // strictly inside a known (finite) duration. A corrupt or foreign value
                // past the end would seek past it — firing `ended` at once — or into an
                // unbuffered gap that never fires `seeked`, wedging startup on the
                // spinner. Anything else (out of range, or duration not yet known)
                // starts fresh; losing the resume point beats not being able to watch.
                var duration = video.duration;
                var canResume = targetTime > 0 &&
                    isFinite(targetTime) &&
                    isFinite(duration) &&
                    targetTime < duration;
                if (canResume) {
                    video.currentTime = targetTime;
                }
                else {
                    video.play();
                    setReady();
                }
                return;
            }
            if (ready) {
                clearBuffering();
                setPlayerState(function (s) { return (__assign(__assign({}, s), { playing: true })); });
            }
        };
        var onSeeked = function () {
            // The resume seek is what completes startup: play from the restored
            // position and only then mark ready.
            if (!ready) {
                video.play();
                setReady();
                return;
            }
            if (video.readyState >= 3) {
                setLoading(false);
            }
        };
        var onPlay = function () {
            if (ready) {
                clearBuffering();
                setPlayerState(function (s) { return (__assign(__assign({}, s), { playing: true })); });
            }
        };
        var onCanPlayThrough = function () {
            if (ready)
                clearBuffering();
        };
        // --- Buffer detection with 1200ms debounce ---
        var bufferTimer = null;
        var showBuffering = function () {
            if (bufferTimer)
                return;
            bufferTimer = setTimeout(function () {
                if (!video.paused && !video.ended) {
                    setLoading(true);
                }
                bufferTimer = null;
            }, 1200);
        };
        var clearBuffering = function () {
            if (bufferTimer) {
                clearTimeout(bufferTimer);
                bufferTimer = null;
            }
            if (ready)
                setLoading(false);
        };
        var onWaiting = function () {
            if (ready)
                showBuffering();
        };
        var onStalled = function () {
            if (ready && !video.paused && !video.ended)
                showBuffering();
        };
        var onPause = function () {
            if (!ready)
                return;
            if (video.readyState < 3) {
                setLoading(true);
                return;
            }
            setPlayerState(function (s) {
                var next = __assign(__assign({}, s), { playing: false });
                if (playerRef.current)
                    updateProgress(videoRef, next);
                return next;
            });
        };
        var onTimeUpdate = function () {
            if (!videoRef.current || !playerRef.current || !ready)
                return;
            var currentTime = videoRef.current.currentTime;
            if (lastTimeCheckSecond === Math.floor(currentTime))
                return;
            lastTimeCheckSecond = Math.floor(currentTime);
            clearBuffering();
            setPlayerState(function (s) {
                if (!s.loaded)
                    return s;
                var dur = videoRef.current.duration;
                var newProgress = dur > 0 ? Math.round((currentTime / dur) * 100) / 100 : 0;
                progressRef.current = newProgress;
                var next = __assign(__assign({}, s), { progress: newProgress });
                var stats = getProgresStats(videoRef.current, next);
                if (stats) {
                    updateStateProgress(stats.progress, stats.timeLeftSide, stats.timeRightSide);
                }
                var lastSaved = currentTimeRef.current;
                currentTimeRef.current = currentTime;
                if ((Math.floor(currentTime) % 15 === 0 ||
                    Math.abs(currentTime - lastSaved) > 15) &&
                    Math.floor(currentTime) !== Math.floor(lastSaved)) {
                    updateProgress(videoRef, next);
                }
                return next;
            });
        };
        var onEnded = function () {
            setPlayerState(function (s) { return (__assign(__assign({}, s), { playing: false, ended: true })); });
        };
        var onAdaptation = function () {
            setPlayerState(function (s) {
                var videoTracks = playerRef.current.getVideoTracks();
                var audioTracks = playerRef.current.getAudioTracks();
                var textTracks = playerRef.current.getTextTracks();
                if (s.language && (audioTracks === null || audioTracks === void 0 ? void 0 : audioTracks.length)) {
                    var track = audioTracks.find(function (t) { return t.lang === s.language; });
                    if (track) {
                        playerRef.current.selectAudioTrack(track.id, true);
                    }
                }
                return __assign(__assign({}, s), { videoTracks: videoTracks, audioTracks: audioTracks, textTracks: textTracks });
            });
        };
        // Every failure after load() resolves — streaming retries exhausted,
        // key-status or DRM failure, the network going away — is delivered only as
        // Shaka's own "error" event; nothing rejects and the default failure
        // callback just logs. Without this listener those are a silent spinner.
        var onShakaError = function (event) {
            var detail = event.detail;
            console.error("[player] shaka error", detail === null || detail === void 0 ? void 0 : detail.code, detail);
            if ((detail === null || detail === void 0 ? void 0 : detail.severity) === SHAKA_SEVERITY_CRITICAL) {
                showFatalError();
            }
        };
        var onLargeGap = function (event) {
            var currentTime = event.currentTime;
            if (Math.abs(currentTime - digitSeekTargetRef.current) < 1) {
                event.preventDefault();
            }
        };
        // --- Stall watchdog ---
        var watchdogTimer = null;
        var lastSampledTime = -1;
        var stalledSince = 0;
        var recoveryAttempts = 0;
        var recovering = false;
        var stallAnchor = 0;
        var recoverFromStall = function () {
            recoveryAttempts++;
            var position = video.currentTime;
            var duration = video.duration;
            // Where this stall episode started. Backward seeks are measured from here
            // rather than from the current position, so repeated attempts do not
            // compound: a permanently wedged box would otherwise walk itself
            // backwards through the asset 3s at a time, and onTimeUpdate persists
            // that drift as the stored resume point.
            if (recoveryAttempts === 1)
                stallAnchor = position;
            // A nudge into or past the end fires `ended` and finishes the movie
            // early, so go straight to the backward rebuffer there.
            var nudgeWouldOvershoot = duration > 0 && position + STALL_NUDGE_SECONDS >= duration - 1;
            // Deepen the rebuffer over the first few attempts so each one asks for a
            // different spot, then hold at that floor for as long as the stall lasts.
            var rebufferSteps = Math.min(Math.max(1, recoveryAttempts - MAX_FORWARD_NUDGES), MAX_REBUFFER_STEPS);
            var target = recoveryAttempts <= MAX_FORWARD_NUDGES && !nudgeWouldOvershoot
                ? position + STALL_NUDGE_SECONDS
                : Math.max(0, stallAnchor - STALL_REBUFFER_SECONDS * rebufferSteps);
            console.warn("[player] stall recovery attempt " +
                recoveryAttempts +
                " — seeking " +
                position.toFixed(1) +
                "s -> " +
                target.toFixed(1) +
                "s");
            video.currentTime = target;
            // Re-baseline on the next tick from where the box actually landed, not
            // from `target`: a nudge it ignores or snaps back from would otherwise
            // read as playback progress and reset the ladder before it ever reached
            // the backward rebuffer seek.
            recovering = true;
            stalledSince = 0;
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(function (error) {
                    console.warn("[player] play() after stall recovery failed", error);
                });
            }
        };
        var watchdogTick = function () {
            // Past the fatal modal there is nothing left to recover, and the
            // readyState gate below would not stop us on its own: a key-status or DRM
            // failure can sit at readyState 4 with data still buffered.
            if (gaveUp)
                return;
            if (!ready || video.paused || video.ended || video.seeking) {
                stalledSince = 0;
                return;
            }
            var currentTime = video.currentTime;
            // Take the new baseline from where the box actually landed after a
            // recovery seek, and never read that jump as progress.
            if (recovering) {
                recovering = false;
                lastSampledTime = currentTime;
                return;
            }
            // Movement in either direction is progress — a user seek backwards is not
            // a stall — which is also what keeps a manual seek from being counted as
            // one without the seek handlers having to say so.
            if (Math.abs(currentTime - lastSampledTime) > STALL_EPSILON_SECONDS) {
                lastSampledTime = currentTime;
                stalledSince = 0;
                recoveryAttempts = 0;
                return;
            }
            var now = Date.now();
            if (stalledSince === 0) {
                stalledSince = now;
                return;
            }
            var timeout = recoveryAttempts === 0 ? FIRST_STALL_TIMEOUT_MS : STALL_TIMEOUT_MS;
            if (now - stalledSince < timeout)
                return;
            console.warn("[player] stall confirmed at " +
                currentTime.toFixed(1) +
                "s — readyState " +
                video.readyState);
            // A silent wedge fires neither `waiting` nor `stalled`, so without this
            // the frame just sits there with no spinner over it.
            showBuffering();
            // Only a wedged-but-buffered pipeline is fixable by seeking. With no
            // future data we are genuinely starved, so leave the spinner to wait it
            // out rather than seek into an unbuffered spot. Recovery never gives up:
            // a long buffering stretch on a slow box is not a dead stream.
            if (video.readyState < 3) {
                // Restart the clock so a starved stretch reports once per timeout
                // instead of on every tick.
                stalledSince = now;
                return;
            }
            recoverFromStall();
        };
        // --- Attach listeners BEFORE load ---
        video.addEventListener("canplay", onCanPlay);
        video.addEventListener("seeked", onSeeked);
        video.addEventListener("play", onPlay);
        video.addEventListener("canplaythrough", onCanPlayThrough);
        video.addEventListener("waiting", onWaiting);
        video.addEventListener("stalled", onStalled);
        video.addEventListener("pause", onPause);
        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("ended", onEnded);
        player.addEventListener("adaptation", onAdaptation);
        player.addEventListener("error", onShakaError);
        player.addEventListener("largegap", onLargeGap);
        if (needsStallWatchdog()) {
            console.warn("[player] stall watchdog armed");
            watchdogTimer = setInterval(watchdogTick, WATCHDOG_INTERVAL_MS);
        }
        // --- Cleanup ---
        var cleanup = function () {
            if (bufferTimer)
                clearTimeout(bufferTimer);
            if (watchdogTimer)
                clearInterval(watchdogTimer);
            video.removeEventListener("canplay", onCanPlay);
            video.removeEventListener("seeked", onSeeked);
            video.removeEventListener("play", onPlay);
            video.removeEventListener("canplaythrough", onCanPlayThrough);
            video.removeEventListener("waiting", onWaiting);
            video.removeEventListener("stalled", onStalled);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("timeupdate", onTimeUpdate);
            video.removeEventListener("ended", onEnded);
            player.removeEventListener("adaptation", onAdaptation);
            player.removeEventListener("error", onShakaError);
            player.removeEventListener("largegap", onLargeGap);
            video.pause();
            player.unload();
            player.destroy();
            playerRef.current = null;
        };
        cleanupRef.current = cleanup;
        // --- 3. Load source ---
        player.load(playerState.source.src).catch(function (error) {
            console.error("Player load error:", error);
            // Superseded and aborted loads are swallowed in the facade, so anything
            // reaching here is terminal.
            showFatalError();
        });
        return function () {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [playerState.quality, playerState.episodeId]);
    // --- Keyboard interaction ---
    useEffect(function () {
        var handleKeyInteraction = function (e) {
            if (idlePromptOpenRef.current)
                return;
            if (!showControls)
                e.preventDefault();
            resetControlsTimer();
            lastInputAtRef.current = Date.now();
            resetIdleTimer();
            var digit = DIGIT_CODES.indexOf(e.code);
            var video = videoRef.current;
            if (digit === -1 || !video || !Number.isFinite(video.duration))
                return;
            digitSeekTargetRef.current = (video.duration / 10) * digit;
            video.currentTime = digitSeekTargetRef.current;
        };
        window.addEventListener("keydown", handleKeyInteraction);
        return function () { return window.removeEventListener("keydown", handleKeyInteraction); };
    }, [showControls, resetControlsTimer, resetIdleTimer]);
    // --- Close player ---
    var closePlayer = useCallback(function () {
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        goBack();
    }, [goBack]);
    var playerCloseRef = useFocusable({
        group: "playerClose",
        index: 0,
        onKeyDown: closePlayer,
        onKeyBack: closePlayer,
    });
    return (React.createElement("div", { className: "player-container" },
        React.createElement("video", { className: "video-player" + (!playerState.loaded ? " video-hidden" : ""), ref: videoRef }),
        React.createElement("div", { id: "player-close", className: "close-container " + (!showControls && "hide-controls"), ref: playerCloseRef, tabIndex: 0 },
            React.createElement("div", { className: "close" })),
        playerState.loaded && (React.createElement("div", { className: "player-controls " + (!showControls && "hide-controls") },
            React.createElement(PlayerProgress, null),
            React.createElement(PlayerControls, { closePlayer: closePlayer, playerState: playerState, setPlayerState: setPlayerState, playerRef: playerRef, videoRef: videoRef }))),
        loading && React.createElement(LoadingSpinner, null),
        showIdlePrompt && (React.createElement("div", { id: "idle-prompt-overlay", tabIndex: 0, className: "modal-overlay", key: "idle-prompt-modal" },
            React.createElement(IdlePromptModal, { onYes: handleIdleYes, onNo: handleIdleNo })))));
};



//////////////////
// WEBPACK FOOTER
// ./src/screens/Player/Player.tsx
// module id = 188
// module chunks = 1